import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './TeamCompBuilderPage.css';
import { useChampions, Champion } from '../../hooks/useChampions';
import DataService from '../../XXX/DataService';
import rolePools from '../../data/championRolePools';
import TeamCompBuilds, { TeamSlot } from './TeamCompBuilds';
import TeamCompBenchmarks from './TeamCompBenchmarks';

type Role = 'top' | 'jgl' | 'mid' | 'bot' | 'sup';

// Map our short role keys to the keys used in rolePools JSON
const ROLE_TO_POOL_KEY: Record<Role, keyof typeof rolePools> = {
  top: 'top',
  jgl: 'jungle',
  mid: 'mid',
  bot: 'bot',
  sup: 'support',
};

interface RoleSlot {
  role: Role;
  label: string;
  icon: string;
  champion: Champion | null;
  locked: boolean;
}

const ROLES: { role: Role; label: string; icon: string }[] = [
  { role: 'top', label: 'Top', icon: '/Images/top_icon.png' },
  { role: 'jgl', label: 'Jungle', icon: '/Images/jgl_icon.png' },
  { role: 'mid', label: 'Mid', icon: '/Images/mid_icon.png' },
  { role: 'bot', label: 'Bot', icon: '/Images/bot_icon.png' },
  { role: 'sup', label: 'Support', icon: '/Images/sup_icon.png' },
];

const TeamCompBuilderPage: React.FC = () => {
  const { searchChampions, championsList } = useChampions();
  const [apiVersion, setApiVersion] = useState<string>('15.9.1');

  // Team state
  const [blueTeam, setBlueTeam] = useState<RoleSlot[]>(
    ROLES.map(r => ({ ...r, champion: null, locked: false }))
  );
  const [redTeam, setRedTeam] = useState<RoleSlot[]>(
    ROLES.map(r => ({ ...r, champion: null, locked: false }))
  );

  // Search state
  const [activeSlot, setActiveSlot] = useState<{
    team: 'blue' | 'red';
    roleIndex: number;
  } | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState<Champion[]>([]);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch latest API version for champion images
  useEffect(() => {
    DataService.fetchLatestVersion().then(version => {
      setApiVersion(version);
    }).catch(() => {});
  }, []);

  // Focus input when a slot is activated
  useEffect(() => {
    if (activeSlot && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [activeSlot]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setActiveSlot(null);
        setSearchInput('');
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getPickedIds = useCallback((team: 'blue' | 'red'): Set<string> => {
    const slots = team === 'blue' ? blueTeam : redTeam;
    const ids = new Set<string>();
    slots.forEach(s => {
      if (s.champion) ids.add(s.champion.id);
    });
    return ids;
  }, [blueTeam, redTeam]);

  // Update suggestions when search input changes
  useEffect(() => {
    if (searchInput.length > 0) {
      const results = searchChampions(searchInput);
      // Filter out already-picked champions on the same team
      const pickedIds = getPickedIds(activeSlot?.team || 'blue');
      setSuggestions(results.filter(c => !pickedIds.has(c.id)));
    } else {
      setSuggestions([]);
    }
  }, [searchInput, searchChampions, activeSlot, getPickedIds]);

  /** Pick a random champion from the role pool, matching against the championsList for image data */
  const pickRandomForRole = (role: Role, excludeIds: Set<string>): Champion | null => {
    const poolKey = ROLE_TO_POOL_KEY[role];
    const pool = rolePools[poolKey] as string[];
    if (!pool || pool.length === 0) return null;

    // Filter out already-used champions
    const available = pool.filter(name => !excludeIds.has(name));
    if (available.length === 0) return null;

    const randomName = available[Math.floor(Math.random() * available.length)];

    // Find champion data from the loaded list (for imageUrl)
    const found = championsList.find(c => c.id === randomName);
    if (found) return found;

    // Fallback: construct a Champion object manually
    return {
      id: randomName,
      name: randomName,
      imageUrl: `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/champion/${randomName}.png`,
    };
  };

  /** Collect champion IDs that are locked on both teams (these must never be overwritten) */
  const getLockedIds = useCallback((): Set<string> => {
    const ids = new Set<string>();
    blueTeam.forEach(s => { if (s.locked && s.champion) ids.add(s.champion.id); });
    redTeam.forEach(s => { if (s.locked && s.champion) ids.add(s.champion.id); });
    return ids;
  }, [blueTeam, redTeam]);

  /** Toggle the lock on a specific slot */
  const handleToggleLock = (
    e: React.MouseEvent,
    team: 'blue' | 'red',
    roleIndex: number
  ) => {
    e.stopPropagation();
    const setTeam = team === 'blue' ? setBlueTeam : setRedTeam;
    setTeam(prev =>
      prev.map((slot, i) =>
        i === roleIndex ? { ...slot, locked: !slot.locked } : slot
      )
    );
  };

  /** Randomize unlocked slots on a single team */
  const randomizeTeam = (team: 'blue' | 'red') => {
    const currentSlots = team === 'blue' ? blueTeam : redTeam;
    const otherSlots = team === 'blue' ? redTeam : blueTeam;
    const setTeam = team === 'blue' ? setBlueTeam : setRedTeam;

    // Start with locked IDs from both teams + all picked IDs on the OTHER team
    const usedIds = getLockedIds();
    otherSlots.forEach(s => { if (s.champion) usedIds.add(s.champion.id); });

    const newSlots = currentSlots.map(slot => {
      if (slot.locked) return slot; // locked → keep
      const champ = pickRandomForRole(slot.role, usedIds);
      if (champ) usedIds.add(champ.id);
      return { ...slot, champion: champ };
    });

    setTeam(newSlots);
    closeSearch();
  };

  /** Randomize unlocked slots on BOTH teams */
  const handleRandomize = () => {
    // Only locked champions are guaranteed to stay
    const usedIds = getLockedIds();

    const newBlue = blueTeam.map(slot => {
      if (slot.locked) return slot;
      const champ = pickRandomForRole(slot.role, usedIds);
      if (champ) usedIds.add(champ.id);
      return { ...slot, champion: champ };
    });

    const newRed = redTeam.map(slot => {
      if (slot.locked) return slot;
      const champ = pickRandomForRole(slot.role, usedIds);
      if (champ) usedIds.add(champ.id);
      return { ...slot, champion: champ };
    });

    setBlueTeam(newBlue);
    setRedTeam(newRed);
    closeSearch();
  };

  /** Clear all champions and locks from both teams */
  const handleClearAll = () => {
    setBlueTeam(ROLES.map(r => ({ ...r, champion: null, locked: false })));
    setRedTeam(ROLES.map(r => ({ ...r, champion: null, locked: false })));
    closeSearch();
  };

  const closeSearch = () => {
    setActiveSlot(null);
    setSearchInput('');
    setSuggestions([]);
  };

  const handleSlotClick = (team: 'blue' | 'red', roleIndex: number) => {
    // If clicking the already-active slot, close it
    if (
      activeSlot &&
      activeSlot.team === team &&
      activeSlot.roleIndex === roleIndex
    ) {
      setActiveSlot(null);
      setSearchInput('');
      setSuggestions([]);
      return;
    }
    setActiveSlot({ team, roleIndex });
    setSearchInput('');
    setSuggestions([]);
  };

  const handleChampionSelect = (champion: Champion) => {
    if (!activeSlot) return;

    const setTeam = activeSlot.team === 'blue' ? setBlueTeam : setRedTeam;
    setTeam(prev =>
      prev.map((slot, i) =>
        i === activeSlot.roleIndex ? { ...slot, champion, locked: true } : slot
      )
    );

    setActiveSlot(null);
    setSearchInput('');
    setSuggestions([]);
  };

  const handleClearSlot = (
    e: React.MouseEvent,
    team: 'blue' | 'red',
    roleIndex: number
  ) => {
    e.stopPropagation();
    const setTeam = team === 'blue' ? setBlueTeam : setRedTeam;
    setTeam(prev =>
      prev.map((slot, i) =>
        i === roleIndex ? { ...slot, champion: null, locked: false } : slot
      )
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setActiveSlot(null);
      setSearchInput('');
      setSuggestions([]);
    } else if (e.key === 'Enter' && suggestions.length > 0) {
      handleChampionSelect(suggestions[0]);
    }
  };

  const getChampionImageUrl = (championId: string) => {
    return `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/champion/${championId}.png`;
  };

  // ── Derive TeamSlot arrays to pass to the builds component ──
  const blueTeamSlots: TeamSlot[] = useMemo(
    () =>
      blueTeam.map(s => ({
        role: s.role,
        label: s.label,
        icon: s.icon,
        championId: s.champion?.id ?? null,
        championName: s.champion?.name ?? null,
      })),
    [blueTeam]
  );

  const redTeamSlots: TeamSlot[] = useMemo(
    () =>
      redTeam.map(s => ({
        role: s.role,
        label: s.label,
        icon: s.icon,
        championId: s.champion?.id ?? null,
        championName: s.champion?.name ?? null,
      })),
    [redTeam]
  );

  const renderSlot = (slot: RoleSlot, index: number, team: 'blue' | 'red') => {
    const isActive =
      activeSlot?.team === team && activeSlot?.roleIndex === index;
    const hasChampion = !!slot.champion;

    return (
      <div
        key={`${team}-${slot.role}`}
        className={`tcb-slot ${team} ${isActive ? 'active' : ''} ${hasChampion ? 'has-champion' : ''} ${slot.locked ? 'locked' : ''}`}
        onClick={() => handleSlotClick(team, index)}
      >
        <div className="tcb-slot-image-wrapper">
          {hasChampion ? (
            <>
              <img
                src={getChampionImageUrl(slot.champion!.id)}
                alt={slot.champion!.name}
                className="tcb-champion-image"
              />
              <button
                className="tcb-clear-btn"
                onClick={e => handleClearSlot(e, team, index)}
                title="Remove champion"
              >
                ×
              </button>
            </>
          ) : (
            <div className="tcb-empty-slot-circle" />
          )}
        </div>
        <span className="tcb-slot-label">
          {hasChampion ? slot.champion!.name : `Pick ${slot.label}`}
        </span>

        {/* Inline search dropdown */}
        {isActive && (
          <div
            className="tcb-search-dropdown"
            ref={dropdownRef}
            onClick={e => e.stopPropagation()}
          >
            <input
              ref={searchInputRef}
              type="text"
              placeholder={`Pick ${slot.label}...`}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="tcb-search-input"
            />
            {suggestions.length > 0 && (
              <ul className="tcb-suggestions-list">
                {suggestions.map(champ => (
                  <li
                    key={champ.id}
                    className="tcb-suggestion-item"
                    onClick={() => handleChampionSelect(champ)}
                  >
                    <img src={champ.imageUrl} alt={champ.name} />
                    <span>{champ.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="tcb-page">
      <h1 className="tcb-title">Team Comp Builder</h1>
      <p className="tcb-subtitle">
        Build full team compositions and compare champions across all roles
      </p>

      <div className="tcb-actions">
        <button className="tcb-randomize-btn" onClick={handleRandomize}>
          Randomize All
        </button>
        <button className="tcb-clear-all-btn" onClick={handleClearAll}>
          Clear All
        </button>
      </div>

      <div className="tcb-teams-container">
        {/* Header row — empty edge columns to align with diamond columns */}
        <div className="tcb-header-row">
          <div /> {/* left diamond spacer */}
          <div className="tcb-team-header blue">
            <h2>Blue Side</h2>
            <button
              className="tcb-team-randomize-btn"
              onClick={() => randomizeTeam('blue')}
              title="Randomize blue side (keeps your picks)"
            >
              🎲
            </button>
          </div>
          <div className="tcb-vs-divider">
            <span>VS</span>
          </div>
          <div className="tcb-team-header red">
            <h2>Red Side</h2>
            <button
              className="tcb-team-randomize-btn"
              onClick={() => randomizeTeam('red')}
              title="Randomize red side (keeps your picks)"
            >
              🎲
            </button>
          </div>
          <div /> {/* right diamond spacer */}
        </div>

        {/* Role rows: diamond – blue – role icon – red – diamond */}
        {ROLES.map((roleDef, i) => (
          <div key={roleDef.role} className="tcb-role-row">
            <div className="tcb-role-row-diamond">
              <button
                className={`tcb-lock-diamond ${blueTeam[i].locked ? 'locked' : ''} ${blueTeam[i].champion ? 'has-champion' : ''} blue`}
                onClick={e => { e.stopPropagation(); handleToggleLock(e, 'blue', i); }}
                title={blueTeam[i].locked ? 'Unlock (will re-roll on randomize)' : 'Lock (keep on randomize)'}
                disabled={!blueTeam[i].champion}
              >
                <span className="tcb-diamond-shape" />
              </button>
            </div>
            <div className="tcb-role-row-slot">
              {renderSlot(blueTeam[i], i, 'blue')}
            </div>
            <div className="tcb-role-row-icon">
              <img src={roleDef.icon} alt={roleDef.label} />
            </div>
            <div className="tcb-role-row-slot">
              {renderSlot(redTeam[i], i, 'red')}
            </div>
            <div className="tcb-role-row-diamond">
              <button
                className={`tcb-lock-diamond ${redTeam[i].locked ? 'locked' : ''} ${redTeam[i].champion ? 'has-champion' : ''} red`}
                onClick={e => { e.stopPropagation(); handleToggleLock(e, 'red', i); }}
                title={redTeam[i].locked ? 'Unlock (will re-roll on randomize)' : 'Lock (keep on randomize)'}
                disabled={!redTeam[i].champion}
              >
                <span className="tcb-diamond-shape" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ───── Builds & Runes (separate component) ───── */}
      <TeamCompBuilds blueTeam={blueTeamSlots} redTeam={redTeamSlots} />

      {/* ───── Power Spike Comparison ───── */}
      <TeamCompBenchmarks blueTeam={blueTeamSlots} redTeam={redTeamSlots} />
    </div>
  );
};

export default TeamCompBuilderPage;
